import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function LoginPage() {
  const { signIn, resetPassword, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('resetToken');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const isResetConfirm = Boolean(resetToken);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (isResetConfirm && resetToken) {
        await confirmPasswordReset(resetToken, password);
        setResetSent(true);
        setPassword('');
        return;
      }
      const user = await signIn(email, password);
      navigate(user.role === 'CLIENT' ? '/portal' : '/admin', { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      if (code.includes('network')) {
        setError("Can't reach the server. Check your connection and try again.");
      } else {
        setError(
          isResetConfirm
            ? 'That reset link is invalid or expired. Request a new one.'
            : "Wrong email or password. Reset your password if you've forgotten it.",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setError(null);
    if (!email) {
      setError('Enter your email above first, then click reset.');
      return;
    }
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch {
      setError("Couldn't send reset email — double-check the address.");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4">
      <div className="w-full max-w-[400px]">
        <div className="flex flex-col items-center mb-8">
          <img src="/favicon-192.png" alt="Ventin Media" className="h-14 w-14 mb-3" />
          <h1 className="text-[32px] font-semibold text-center leading-[1.2]">Ventin Support</h1>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              {isResetConfirm ? 'Set new password' : 'Sign in'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              {!isResetConfirm && (
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete={isResetConfirm ? 'new-password' : 'current-password'}
                  required
                  minLength={isResetConfirm ? 12 : undefined}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? 'Working…' : isResetConfirm ? 'Set password' : 'Sign in'}
              </Button>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {resetSent && (
                <Alert>
                  <AlertDescription>
                    {isResetConfirm
                      ? 'Password updated. You can now sign in.'
                      : 'Password reset email sent — check your inbox.'}
                  </AlertDescription>
                </Alert>
              )}
              {!isResetConfirm && (
                <button
                  type="button"
                  onClick={onReset}
                  className="text-sm text-primary hover:underline w-full text-center"
                >
                  Forgot your password?
                </button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
